import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ROLES } from "../../config/roles";
import { agentService } from "../../services/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AgentJoinRequestModal from "../agency/AgentJoinRequestModal";

const AgencyDashboard = ({ user, kycVerified }) => {
  const { currentRole } = useSelector((state) => state.auth);
  const [agentJoinRequests, setAgentJoinRequests] = useState([]);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  // W1-01: session-local banner dismissals (X). Purely a view state — the
  // request itself stays pending on the server; the real resolution happens
  // through accept/decline (the pending-requests refetch then drops the row).
  const [dismissedRequestIds, setDismissedRequestIds] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAgentJoinRequests = async () => {
      setLoading(true);
      try {
        const response = await agentService.getPendingAgentRequests();
        if (response.success) {
          setAgentJoinRequests(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching agent join requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAgentJoinRequests();
  }, []);

  // Only show for agency admins
  if (currentRole !== ROLES.AgencyAdmin) {
    return null;
  }

  const handleRequestAction = (requestId, action) => {
    // Remove the request from the list
    setAgentJoinRequests((prev) =>
      prev.filter((req) => req.id !== requestId)
    );

    // If no more requests, close the modal
    if (agentJoinRequests.length <= 1) {
      setShowRequestsModal(false);
    }
  };

  // W1-01: banners the user has not dismissed this session. The old UI painted
  // the requests as an absolute-positioned fake "stack" where only the top
  // banner was reachable — now every request renders as a real, individually
  // usable banner: one when collapsed, all of them when expanded.
  const visibleRequests = agentJoinRequests.filter(
    (request) => !dismissedRequestIds.includes(request.id)
  );
  const shownRequests = expanded ? visibleRequests : visibleRequests.slice(0, 1);

  const handleDismissBanner = (requestId) => {
    setDismissedRequestIds((prev) => [...prev, requestId]);
  };

  const openRequest = (request) => {
    // Navigate to agent profile with request state (accept/decline lives
    // there); on return the mount refetch drops resolved requests, so a
    // completed action auto-removes its banner.
    navigate(`/search/agent/${request.agent.account_id}`, {
      state: { agentJoinRequest: request },
    });
  };

  const renderBannerIcon = (gradientId) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <rect width="24" height="24" rx="6" fill={`url(#${gradientId})`} />
      <path d="M12 9V14" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1" fill="white" />
      <defs>
        <linearGradient
          id={gradientId}
          x1="12"
          y1="0"
          x2="12"
          y2="24"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#DBA948" />
          <stop offset="0.0001" stopColor="#FFC46B" />
          <stop offset="1" stopColor="#FFA318" />
        </linearGradient>
      </defs>
    </svg>
  );

  return (
    <div className={`mb-8 flex flex-col gap-6 ${kycVerified ? "mt-6" : ""}`}>
      {visibleRequests.length > 0 && (
        <div className="flex flex-col gap-3">
          {shownRequests.map((request) => (
            <div
              key={request.id}
              className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 p-4 md:px-6 md:py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl w-full max-w-[1400px]"
              style={{ boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)" }}
            >
              <div className="flex items-center gap-2 min-w-0">
                {renderBannerIcon(`agent-join-banner-icon-${request.id}`)}
                <span className="text-sm md:text-base font-medium">
                  {`Agent "${request.agent.name}" has requested to join your agency.`}
                </span>
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto flex-shrink-0">
                <button
                  type="button"
                  className="bg-[#FFB039] hover:bg-[#F5A423] px-4 py-2 rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-colors flex-1 md:flex-none"
                  onClick={() => openRequest(request)}
                >
                  <span className="text-white font-bold">Review request</span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.33398 6.00016H10.6673M10.6673 6.00016L6.00065 1.3335M10.6673 6.00016L6.00065 10.6668"
                      stroke="white"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Dismiss"
                  title="Dismiss"
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-[#B58A3F] hover:bg-[#F7E7C8] transition-colors flex-shrink-0"
                  onClick={() => handleDismissBanner(request.id)}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}

          {/* Step-through control: the remaining requests are real banners one
              click away, not unreachable edges behind the top one. */}
          {visibleRequests.length > 1 && (
            <button
              type="button"
              className="self-start text-sm font-medium text-[#B58A3F] hover:text-[#8F6C2E] px-1"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? (
                "Show less"
              ) : (
                <>
                  {"Show all"}
                  {` (${visibleRequests.length})`}
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Agent Join Requests Modal */}
      {showRequestsModal && (
        <AgentJoinRequestModal
          requests={agentJoinRequests}
          onClose={() => setShowRequestsModal(false)}
          onRequestAction={handleRequestAction}
        />
      )}
    </div>
  );
};

export default AgencyDashboard; 